import { Form, DatePicker, Input, Select, message, Upload, Checkbox } from "antd";
import type { UploadFile } from "antd";
import React, { FC, useState, useMemo, useEffect } from "react";
import { Package, MapPin, Calendar, FileText, X, Plus, Upload as UploadIcon } from "lucide-react";
import { useAuth } from "~/store";
import dayjs from "dayjs";
import { TInventory } from "~/types";
import { useGetMyInventories } from "~/services/inventory";
import { CheckType, InventoryStatusCode, ServiceType, DocumentType } from "~/utils/constants";
import { InventorySelectionSidebar } from "../overview/inventory-selection-sidebar";
import { capitalize } from "~/utils";
import { fileServices } from "~/services/file";
import { useGetSubClients, useGetClient } from "~/services/client/client.queries";
import { useUpdateClient } from "~/services/client/client.mutations";
import { useCreateServiceTicket } from "~/services/service-ticket/service-ticket.mutations";
import { useQueryClient } from "@tanstack/react-query";
import { JobStatus, Role } from "~/utils/constants";
import { userService } from "~/services/user/user.service";
import { notificationsAPI } from "~/services/notifications/notifications.api";
import { BOOKING_KEYS } from "~/services/booking";
import { TBooking } from "~/types";
import { useGetBooking } from "~/services/booking";

const getStatusBadge = (status: InventoryStatusCode) => {
  const statusConfig = {
    [InventoryStatusCode.AVAILABLE_ON_HAND]: {
      text: "Available On Hand",
      className: "bg-[#1a1a2e] text-white",
    },
    [InventoryStatusCode.AVAILABLE_LIVE]: {
      text: "Available Live",
      className: "bg-[#1a1a2e] text-white",
    },
    [InventoryStatusCode.NO_POSSESSION]: {
      text: "No Possession",
      className: "bg-gray-200 text-[#1a1a2e]",
    },
    [InventoryStatusCode.RELEASED]: {
      text: "Released",
      className: "bg-gray-200 text-[#1a1a2e]",
    },
  };
  const config =
    statusConfig[status] ||
    statusConfig[InventoryStatusCode.NO_POSSESSION];
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${config.className}`}
    >
      {config.text}
    </span>
  );
};

const { TextArea } = Input;
const { Option } = Select;

interface AddServiceSidebarProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  booking?: TBooking | null;
  preSelectedInventoryIds?: string[];
}

type MovementType = "delivery_from_storage" | "collect_for_storage" | "transport" | "on_site_service";

export const AddServiceSidebar: FC<AddServiceSidebarProps> = ({
  open,
  onClose,
  bookingId,
  booking: bookingProp,
  preSelectedInventoryIds = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isInventorySidebarOpen, setIsInventorySidebarOpen] = useState(false);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState<string[]>([]);
  const [movementType, setMovementType] = useState<MovementType | undefined>(undefined);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { user } = useAuth();
  const client = user?.client;
  const createServiceTicketMutation = useCreateServiceTicket();
  const updateClientMutation = useUpdateClient();
  const queryClient = useQueryClient();
  const [preferredTimeWindowCheckbox, setPreferredTimeWindowCheckbox] = useState<string[]>([]);

  // Fetch booking if not provided as prop
  const { data: bookingData } = useGetBooking(bookingProp ? undefined : bookingId);
  const booking = bookingProp || bookingData;

  // Fetch subclients
  const { data: subClientsData } = useGetSubClients(client?.id || "");

  // Automatically determine subclient from booking
  const bookingSubClientId = useMemo(() => {
    if (!booking?.clientId || !client?.id) return undefined;
    // If booking's clientId is different from main client's id, it's a subclient
    if (booking.clientId !== client.id) {
      // Verify it's actually a subclient by checking subclients list
      const isSubClient = subClientsData?.items?.some(
        (subClient) => subClient.id === booking.clientId
      );
      return isSubClient ? booking.clientId : undefined;
    }
    return undefined;
  }, [booking?.clientId, client?.id, subClientsData?.items]);

  // Fetch selected subclient data to get their shipping addresses
  const { data: selectedSubClientData } = useGetClient(bookingSubClientId);

  // Determine which client's addresses to use
  const addressesToUse = useMemo(() => {
    if (bookingSubClientId && selectedSubClientData?.shippingAddresses) {
      return selectedSubClientData.shippingAddresses;
    }
    return client?.shippingAddresses || [];
  }, [bookingSubClientId, selectedSubClientData, client?.shippingAddresses]);

  // Fetch available inventory items - filter by subclient if booking is for a subclient
  const { data: inventoryData } = useGetMyInventories({
    checkParentId: CheckType.UNCHECK,
    status: InventoryStatusCode.AVAILABLE_ON_HAND,
    page: 1,
    pageSize: 100,
    subClientId: bookingSubClientId,
  });

  const selectedInventories = useMemo(() => {
    if (!inventoryData?.items) return [];
    return inventoryData.items.filter((item) =>
      selectedInventoryIds.includes(item.id)
    );
  }, [inventoryData?.items, selectedInventoryIds]);

  // Map movement type to ServiceType
  const mapMovementTypeToServiceType = (movementType: MovementType): ServiceType => {
    switch (movementType) {
      case "delivery_from_storage":
        return ServiceType.DELIVERY;
      case "collect_for_storage":
        return ServiceType.COLLECT;
      case "transport":
        return ServiceType.TRANSPORT;
      case "on_site_service":
        return ServiceType.ON_SITE;
      default:
        return ServiceType.DELIVERY;
    }
  };

  // Initialize selected inventory when sidebar opens or preSelectedInventoryIds changes
  useEffect(() => {
    if (open && preSelectedInventoryIds.length > 0) {
      setSelectedInventoryIds(preSelectedInventoryIds);
    }
  }, [open, preSelectedInventoryIds]);

  // Format request notes
  const formatRequestNotes = (
    values: any,
    selectedInventory: TInventory[]
  ): string => {
    const notes: string[] = [];
    notes.push("=== CLIENT SERVICE REQUEST ===");
    notes.push("");
    
    // Contact Information
    notes.push("CONTACT INFORMATION:");
    notes.push(`Name: ${values.contactName || `${client?.firstName} ${client?.lastName}`}`);
    notes.push(`Phone: ${values.contactPhone || client?.contactPhone || "N/A"}`);
    notes.push("");
    
    // Movement Type
    notes.push("MOVEMENT TYPE:");
    const movementTypeLabels: Record<MovementType, string> = {
      delivery_from_storage: "Delivery from Storage",
      collect_for_storage: "Collect for Storage/Hold",
      transport: "Transport",
      on_site_service: "On Site Service",
    };
    notes.push(movementTypeLabels[values.movementType] || values.movementType);
    notes.push("");
    
    // Addresses
    if (values.pickupAddress) {
      notes.push("PICKUP ADDRESS:");
      notes.push(values.pickupAddress);
      notes.push("");
    }
    if (values.deliveryAddress) {
      notes.push("DELIVERY ADDRESS:");
      notes.push(values.deliveryAddress);
      notes.push("");
    }
    if (values.serviceAddress) {
      notes.push("SERVICE ADDRESS:");
      notes.push(values.serviceAddress);
      notes.push("");
    }
    
    // Preferred Date & Time
    notes.push("PREFERRED DATE & TIME:");
    // Add preferred time window checkboxes
    if (preferredTimeWindowCheckbox.length > 0) {
      const timeWindowLabels: Record<string, string> = {
        ASAP: "ASAP",
        within_1_week: "Within 1 Week",
        "2_weeks": "2 Weeks",
        beyond_2_weeks: "Beyond 2 Weeks",
      };
      const labels = preferredTimeWindowCheckbox.map((val) => timeWindowLabels[val] || val);
      notes.push(`Preferred Time Window: ${labels.join(", ")}`);
    }
    if (values.preferredDate) {
      notes.push(`Preferred Date: ${dayjs(values.preferredDate).format("MMMM D, YYYY")}`);
    }
    if (values.preferredTimeWindow) {
      notes.push(`Preferred Time of Day: ${values.preferredTimeWindow}`);
    }
    notes.push("");
    
    // Inventory Items
    if (selectedInventory.length > 0) {
      notes.push("INVENTORY ITEMS:");
      selectedInventory.forEach((item, index) => {
        notes.push(`${index + 1}. ${item.inventoryRef || "No Ref"}`);
        if (item.clientInternalRef) {
          notes.push(`   Client Ref: ${item.clientInternalRef}`);
        }
        if (item.title) {
          notes.push(`   Title: ${item.title}`);
        }
        if (item.artistName) {
          notes.push(`   Artist: ${item.artistName}`);
        }
      });
      notes.push("");
    }
    
    // Special Instructions
    if (values.specialInstructions) {
      notes.push("SPECIAL INSTRUCTIONS:");
      notes.push(values.specialInstructions);
      notes.push("");
    }
    
    // Documents
    if (fileList.length > 0) {
      notes.push("ATTACHED DOCUMENTS:");
      fileList.forEach((file, index) => {
        notes.push(`${index + 1}. ${file.name} (${file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "N/A"})`);
      });
      notes.push("");
    }
    
    notes.push("=== END OF REQUEST ===");
    return notes.join("\n");
  };

  // Parse address string into components
  const parseAddressString = (addressString: string) => {
    // Simple parsing - can be enhanced
    const parts = addressString.split(',').map(p => p.trim());
    return {
      address: parts[0] || "",
      city: parts[1] || "",
      state: parts[2]?.split(" ")[0] || "",
      zip: parts[2]?.split(" ")[1] || "",
    };
  };

  // Save new address to client profile (subclient if booking is for subclient, otherwise main client)
  const saveNewAddress = async (addressString: string, addressType: 'pickup' | 'delivery' | 'service') => {
    const targetClientId = bookingSubClientId || client?.id;
    if (!targetClientId) return;

    try {
      const addressParts = parseAddressString(addressString);
      const newAddress = {
        address: addressParts.address,
        city: addressParts.city,
        state: addressParts.state,
        zip: addressParts.zip,
        addressName: `${addressType.charAt(0).toUpperCase() + addressType.slice(1)} Address`,
      };

      // Get current addresses from the correct client
      const currentClient = bookingSubClientId ? selectedSubClientData : client;
      const currentAddresses = currentClient?.shippingAddresses || [];
      const updatedAddresses = [...currentAddresses, newAddress];

      const formData = new FormData();
      formData.append('shippingAddresses', JSON.stringify(updatedAddresses));

      await updateClientMutation.mutateAsync({
        id: targetClientId,
        formData,
      });

      // Refresh the subclient data if we saved to a subclient
      if (bookingSubClientId) {
        queryClient.invalidateQueries({
          queryKey: ["clientManagement/client", bookingSubClientId],
        });
      } else {
        // Refresh user data for main client
        const { authStore } = await import("~/store");
        const updatedUser = await userService.getUsers({ 
          roles: [Role.CLIENT],
          search: client?.id,
          pageSize: 1 
        });
        if (updatedUser.items.length > 0) {
          authStore.getState().setMe(updatedUser.items[0]);
        }
      }
    } catch (error) {
      console.error("Error saving new address:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (selectedInventoryIds.length === 0) {
        message.error("Please select at least one inventory item");
        setLoading(false);
        return;
      }

      const selectedInventory = selectedInventories;
      const serviceType = mapMovementTypeToServiceType(values.movementType);
      const notes = formatRequestNotes(values, selectedInventory);

      // Determine location data
      let locationId: string | undefined = undefined;
      let tempLocationData: any = undefined;

      if (values.movementType === "delivery_from_storage" && values.deliveryAddress) {
        // Check if it's a saved address
        const savedAddress = addressesToUse.find(
          (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.deliveryAddress
        );
        if (savedAddress) {
          locationId = savedAddress.id;
        } else {
          // Parse and save new address
          const addressParts = parseAddressString(values.deliveryAddress);
          tempLocationData = {
            address: addressParts.address,
            city: addressParts.city,
            state: addressParts.state,
            zip: addressParts.zip,
          };
          await saveNewAddress(values.deliveryAddress, 'delivery');
        }
      } else if (values.movementType === "collect_for_storage" && values.pickupAddress) {
        const savedAddress = addressesToUse.find(
          (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.pickupAddress
        );
        if (savedAddress) {
          locationId = savedAddress.id;
        } else {
          const addressParts = parseAddressString(values.pickupAddress);
          tempLocationData = {
            address: addressParts.address,
            city: addressParts.city,
            state: addressParts.state,
            zip: addressParts.zip,
          };
          await saveNewAddress(values.pickupAddress, 'pickup');
        }
      } else if (values.movementType === "transport") {
        if (values.deliveryAddress) {
          const savedAddress = addressesToUse.find(
            (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.deliveryAddress
          );
          if (savedAddress) {
            locationId = savedAddress.id;
          } else {
            const addressParts = parseAddressString(values.deliveryAddress);
            tempLocationData = {
              address: addressParts.address,
              city: addressParts.city,
              state: addressParts.state,
              zip: addressParts.zip,
            };
            await saveNewAddress(values.deliveryAddress, 'delivery');
          }
        }
      } else if (values.movementType === "on_site_service" && values.serviceAddress) {
        const savedAddress = addressesToUse.find(
          (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.serviceAddress
        );
        if (savedAddress) {
          locationId = savedAddress.id;
        } else {
          const addressParts = parseAddressString(values.serviceAddress);
          tempLocationData = {
            address: addressParts.address,
            city: addressParts.city,
            state: addressParts.state,
            zip: addressParts.zip,
          };
          await saveNewAddress(values.serviceAddress, 'service');
        }
      }

      // Step 1: Create service ticket under existing booking
      // Use booking's subclient if it exists, otherwise use main client
      const serviceClientId = bookingSubClientId || client?.id;
      if (!serviceClientId) {
        throw new Error("Client ID is required");
      }

      const serviceTicketPayload: any = {
        jobTicketId: bookingId,
        clientId: serviceClientId,
        serviceType: serviceType,
        serviceDatetime: values.preferredDate ? values.preferredDate.toISOString() : undefined,
        status: JobStatus.ESTIMATED,
        shippingAddressId: locationId,
        tempLocationData: tempLocationData,
        staffNotes: notes,
        inventoryIds: selectedInventoryIds,
        skipEmailNotifications: true,
      };

      const serviceTicket = await createServiceTicketMutation.mutateAsync(serviceTicketPayload);

      // Step 2: Upload documents if any - link to the service ticket
      if (fileList.length > 0 && serviceTicket?.id) {
        const uploadPromises = fileList.map(async (file) => {
          if (file.originFileObj) {
            return fileServices.createFile({
              title: file.name || "Service Request Document",
              type: DocumentType.SUPPLEMENTARY,
              file: file.originFileObj,
              serviceTicketId: serviceTicket.id,
              visibleToStaff: true,
            });
          }
        });

        await Promise.all(uploadPromises);
      }

      // Send notification to all admins
      try {
        const adminUsers = await userService.getUsers({
          roles: [Role.ADMIN, Role.SUPER_ADMIN],
          page: 1,
          pageSize: 1000,
        });

        if (adminUsers.items.length > 0) {
          const adminUserIds = adminUsers.items.map((user) => user.id);
          
          const notificationSubject = `New Service Added to Booking ${bookingId} from ${client?.companyName || `${client?.firstName} ${client?.lastName}`.trim() || 'Client'}`;
          const notificationBody = `A new service has been added to an existing booking:\n\n${notes}\n\nService Ticket: ${serviceTicket?.serial || 'Pending'}`;

          await notificationsAPI.sendManualMessage({
            subject: notificationSubject,
            body: notificationBody,
            recipientIds: adminUserIds,
            sendMode: 'individual',
          });
        }
      } catch (notificationError) {
        console.error('Error sending notification to admins:', notificationError);
      }

      message.success(
        `Service added successfully! Service ticket ${serviceTicket?.serial ? `(${serviceTicket.serial})` : ''} has been created with status ESTIMATED. Our team will contact you to confirm the details.`
      );
      
      form.resetFields();
      setSelectedInventoryIds([]);
      setFileList([]);
      setPreferredTimeWindowCheckbox([]);
      
      // Invalidate booking queries to refresh the booking detail
      queryClient.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
      });
      
      onClose();
    } catch (error: any) {
      console.error("Error adding service:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to add service. Please try again.";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || createServiceTicketMutation.isPending;

  // Rest of the component is identical to RequestServiceSidebar...
  // I'll copy the form JSX from RequestServiceSidebar
  return (
    <div className="h-full relative">
      <div
        className={`h-full w-full bg-white border-r border-[#e5e5e5] flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ boxShadow: "4px 0 12px rgba(0,0,0,0.15)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#e5e5e5] bg-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <Package size={20} className="text-[#1a1a2e]" />
            <h2 className="text-lg font-bold uppercase tracking-wide text-[#1a1a2e]">
              Add Service to Booking
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#fafafa] transition-colors flex-shrink-0 ml-2"
            aria-label="Close sidebar"
          >
            <X size={20} className="text-[#1a1a2e]" />
          </button>
        </div>

        {/* Content - Same form as RequestServiceSidebar */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <Form 
            form={form} 
            layout="vertical" 
            initialValues={{ movementType: "delivery_from_storage" }}
            onValuesChange={(changedValues) => {
              if (changedValues.movementType !== undefined) {
                setMovementType(changedValues.movementType);
                form.setFieldsValue({
                  pickupAddress: undefined,
                  deliveryAddress: undefined,
                  serviceAddress: undefined,
                });
              }
            }}
          >
            {/* Movement Type */}
            <Form.Item
              name="movementType"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                  Movement Type
                </span>
              }
              rules={[{ required: true, message: "Please select a movement type" }]}
            >
              <Select
                placeholder="Select movement type"
                className="border border-[#e5e5e5] rounded-lg"
                onChange={(value) => setMovementType(value)}
              >
                <Option value="delivery_from_storage">Delivery from Storage</Option>
                <Option value="collect_for_storage">Collect for Storage/Hold</Option>
                <Option value="transport">Transport</Option>
                <Option value="on_site_service">On Site Service</Option>
              </Select>
            </Form.Item>

            {/* Inventory Selection */}
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                  <Package size={14} />
                  Inventory Items
                </span>
              }
            >
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setIsInventorySidebarOpen(true)}
                  className="w-full border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-all flex items-center justify-center gap-2"
                >
                  <Plus size={16} />
                  Select Inventory Items
                </button>
                {selectedInventories.length > 0 && (
                  <div className="border border-[#e5e5e5] rounded-lg bg-white p-3 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                      Selected ({selectedInventories.length})
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedInventories.map((item) => (
                        <div
                          key={item.id}
                          className="p-2 border border-[#e5e5e5] rounded-lg bg-white space-y-1"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold uppercase tracking-wide text-[#1a1a2e] truncate">
                                {item.inventoryRef || "No Ref"}
                              </div>
                              {item.clientInternalRef && (
                                <div className="text-xs font-medium text-[#1a1a2e] truncate mt-0.5">
                                  Client Ref: {item.clientInternalRef}
                                </div>
                              )}
                              {item.title && (
                                <div className="text-xs font-medium text-[#1a1a2e] truncate mt-0.5">
                                  {item.title}
                                </div>
                              )}
                              <div className="mt-1.5">
                                {getStatusBadge(item.status)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedInventoryIds((prev) =>
                                  prev.filter((id) => id !== item.id)
                                )
                              }
                              className="ml-2 p-1 hover:bg-red-100 rounded"
                            >
                              <X size={14} className="text-red-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Form.Item>

            {/* Address Fields - Conditional based on movement type */}
            {movementType === "transport" && (
              <Form.Item
                name="pickupAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                    <MapPin size={14} />
                    Pickup Address
                  </span>
                }
                rules={[{ required: true, message: "Please enter pickup address" }]}
              >
                <Select
                  placeholder="Select or enter pickup address"
                  className="border border-[#e5e5e5] rounded-lg ant-select-selection-item-content-overflow"
                  showSearch
                  filterOption={false}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div className="p-2 border-t border-[#e5e5e5]">
                        <Input
                          placeholder="Enter new address"
                          onPressEnter={(e) => {
                            const value = (e.target as HTMLInputElement).value;
                            if (value) {
                              form.setFieldsValue({ pickupAddress: value });
                              saveNewAddress(value, 'pickup');
                            }
                          }}
                          className="border border-[#e5e5e5] rounded-lg"
                        />
                      </div>
                    </>
                  )}
                >
                  {addressesToUse.map((addr, index) => {
                    const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                    return (
                      <Option key={addr.id || index} value={fullAddress}>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                          <div className="text-xs text-[#1a1a2e] break-words">
                            {fullAddress}
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            {(movementType === "delivery_from_storage" || movementType === "transport") && (
              <Form.Item
                name="deliveryAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                    <MapPin size={14} />
                    Delivery Address
                  </span>
                }
                rules={[{ required: true, message: "Please enter delivery address" }]}
              >
                <Select
                  placeholder="Select or enter delivery address"
                  className="border border-[#e5e5e5] rounded-lg ant-select-selection-item-content-overflow"
                  showSearch
                  filterOption={false}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div className="p-2 border-t border-[#e5e5e5]">
                        <Input
                          placeholder="Enter new address"
                          onPressEnter={(e) => {
                            const value = (e.target as HTMLInputElement).value;
                            if (value) {
                              form.setFieldsValue({ deliveryAddress: value });
                              saveNewAddress(value, 'delivery');
                            }
                          }}
                          className="border border-[#e5e5e5] rounded-lg"
                        />
                      </div>
                    </>
                  )}
                >
                  {addressesToUse.map((addr, index) => {
                    const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                    return (
                      <Option key={addr.id || index} value={fullAddress}>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                          <div className="text-xs text-[#1a1a2e] break-words">
                            {fullAddress}
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            {movementType === "collect_for_storage" && (
              <Form.Item
                name="pickupAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                    <MapPin size={14} />
                    Pickup Address
                  </span>
                }
                rules={[{ required: true, message: "Please enter pickup address" }]}
              >
                <Select
                  placeholder="Select or enter pickup address"
                  className="border border-[#e5e5e5] rounded-lg ant-select-selection-item-content-overflow"
                  showSearch
                  filterOption={false}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div className="p-2 border-t border-[#e5e5e5]">
                        <Input
                          placeholder="Enter new address"
                          onPressEnter={(e) => {
                            const value = (e.target as HTMLInputElement).value;
                            if (value) {
                              form.setFieldsValue({ pickupAddress: value });
                              saveNewAddress(value, 'pickup');
                            }
                          }}
                          className="border border-[#e5e5e5] rounded-lg"
                        />
                      </div>
                    </>
                  )}
                >
                  {addressesToUse.map((addr, index) => {
                    const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                    return (
                      <Option key={addr.id || index} value={fullAddress}>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                          <div className="text-xs text-[#1a1a2e] break-words">
                            {fullAddress}
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            {movementType === "on_site_service" && (
              <Form.Item
                name="serviceAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                    <MapPin size={14} />
                    Service Address
                  </span>
                }
                rules={[{ required: true, message: "Please enter service address" }]}
              >
                <Select
                  placeholder="Select or enter service address"
                  className="border border-[#e5e5e5] rounded-lg ant-select-selection-item-content-overflow"
                  showSearch
                  filterOption={false}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div className="p-2 border-t border-[#e5e5e5]">
                        <Input
                          placeholder="Enter new address"
                          onPressEnter={(e) => {
                            const value = (e.target as HTMLInputElement).value;
                            if (value) {
                              form.setFieldsValue({ serviceAddress: value });
                              saveNewAddress(value, 'service');
                            }
                          }}
                          className="border border-[#e5e5e5] rounded-lg"
                        />
                      </div>
                    </>
                  )}
                >
                  {addressesToUse.map((addr, index) => {
                    const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                    return (
                      <Option key={addr.id || index} value={fullAddress}>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                          <div className="text-xs text-[#1a1a2e] break-words">
                            {fullAddress}
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            {/* Preferred Time Window - Checkboxes FIRST */}
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                  Preferred Time Window
                </span>
              }
            >
              <div className="space-y-3">
                <Checkbox.Group
                  className="grid grid-cols-2 gap-2 w-full"
                  value={preferredTimeWindowCheckbox}
                  onChange={(checkedValues) => {
                    setPreferredTimeWindowCheckbox(checkedValues as string[]);
                  }}
                >
                  <Checkbox value="ASAP" className="neo-brutalist-checkbox">
                    ASAP
                  </Checkbox>
                  <Checkbox value="within_1_week" className="neo-brutalist-checkbox">
                    Within 1 Week
                  </Checkbox>
                  <Checkbox value="2_weeks" className="neo-brutalist-checkbox">
                    2 Weeks
                  </Checkbox>
                  <Checkbox value="beyond_2_weeks" className="neo-brutalist-checkbox">
                    Beyond 2 Weeks
                  </Checkbox>
                </Checkbox.Group>
              </div>
            </Form.Item>

            {/* Preferred Date - Not mandatory */}
            <Form.Item
              name="preferredDate"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                  <Calendar size={14} />
                  Preferred Date (Optional)
                </span>
              }
            >
              <DatePicker
                className="w-full border border-[#e5e5e5] rounded-lg"
                disabledDate={(current) => current && current < dayjs().startOf("day")}
                format="MMMM D, YYYY"
              />
            </Form.Item>

            {/* Preferred Time of Day - Not mandatory */}
            <Form.Item
              name="preferredTimeWindow"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                  Preferred Time of Day (Optional)
                </span>
              }
            >
              <Select
                placeholder="Select preferred time (e.g., Morning)"
                className="border border-[#e5e5e5] rounded-lg [&_.ant-select-selector]:!min-h-[42px] [&_.ant-select-selection-item]:!truncate [&_.ant-select-selection-item]:!max-w-full"
                value={form.getFieldValue("preferredTimeWindow")}
                onChange={(value) => {
                  form.setFieldsValue({ preferredTimeWindow: value });
                }}
                allowClear
              >
                <Option value="morning">Morning (8am - 12pm)</Option>
                <Option value="afternoon">Afternoon (12pm - 5pm)</Option>
                <Option value="flexible">Flexible</Option>
              </Select>
            </Form.Item>

            {/* Contact Information */}
            <div className="grid grid-cols-1 gap-4">
              <Form.Item
                name="contactName"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                    Contact Name
                  </span>
                }
                initialValue={client ? `${client.firstName} ${client.lastName}` : ""}
              >
                <Input
                  placeholder="Contact name for this service"
                  className="border border-[#e5e5e5] rounded-lg"
                />
              </Form.Item>
              <Form.Item
                name="contactPhone"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                    Contact Phone
                  </span>
                }
                initialValue={client?.contactPhone || ""}
              >
                <Input
                  placeholder="Contact phone for this service"
                  className="border border-[#e5e5e5] rounded-lg"
                />
              </Form.Item>
            </div>

            {/* Special Instructions */}
            <Form.Item
              name="specialInstructions"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                  <FileText size={14} />
                  Special Instructions (Optional)
                </span>
              }
            >
              <TextArea
                rows={4}
                placeholder="Any special instructions or requirements..."
                className="border border-[#e5e5e5] rounded-lg"
              />
            </Form.Item>

            {/* Document Upload */}
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] flex items-center gap-2">
                  <UploadIcon size={14} />
                  Documents (Optional)
                </span>
              }
            >
              <Upload
                fileList={fileList}
                beforeUpload={(file) => {
                  // Only allow PDFs
                  const isPDF = file.type === "application/pdf";
                  if (!isPDF) {
                    message.error("Only PDF files are allowed");
                    return Upload.LIST_IGNORE;
                  }
                  // Check file size (10MB max)
                  const isLt10M = file.size / 1024 / 1024 < 10;
                  if (!isLt10M) {
                    message.error("File must be smaller than 10MB");
                    return Upload.LIST_IGNORE;
                  }
                  return false; // Prevent auto upload
                }}
                onChange={({ fileList: newFileList }) => {
                  setFileList(newFileList);
                }}
                maxCount={10}
                accept=".pdf"
              >
                <button
                  type="button"
                  className="w-full border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-all flex items-center justify-center gap-2"
                >
                  <UploadIcon size={16} />
                  Upload PDF Documents (Max 10MB)
                </button>
              </Upload>
            </Form.Item>

            {/* Submit Button */}
            <div className="flex gap-3 mt-6 mb-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex-1 border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Submitting..." : "Add Service"}
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Inventory Selection Sidebar (nested) */}
      {isInventorySidebarOpen && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-white transition-all duration-300 ease-in-out border-r border-[#e5e5e5] ${
              isInventorySidebarOpen ? "w-[400px] translate-x-0" : "w-[400px] translate-x-full"
            }`}
            style={{ zIndex: 10 }}
          >
            <InventorySelectionSidebar
              selectedInventoryIds={selectedInventoryIds}
              onSelect={(ids) => {
                setSelectedInventoryIds(ids);
                // Don't close sidebar here - let user click "Done" button
              }}
              onClose={() => setIsInventorySidebarOpen(false)}
              subClientId={bookingSubClientId}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[55] lg:hidden"
            onClick={() => setIsInventorySidebarOpen(false)}
          />
        </>
      )}
    </div>
  );
};

